import type ISystemUser from '@/types/SystemUser'
import type { NavigationUser } from '@/types/navigation'
import type { PresenceStatus, UserPresenceEntry } from '@/types/presence'

/** Estado de quem está no canal. `em_chat` = online com um chat do canal aberto. */
export type EstadoNoCanal = 'ausente' | 'em_chat' | 'online'

export const ESTADO_NO_CANAL_LABEL: Record<EstadoNoCanal, string> = {
  ausente: 'Ausente',
  em_chat: 'Em chat',
  online: 'Online',
}

export interface PresencaNoCanal {
  user: ISystemUser
  estado: EstadoNoCanal
  /** Epoch em MILISSEGUNDOS (navegação). Não confundir com `last_seen_at` da presença (segundos). */
  entered_at: number
}

/**
 * Precedência = precedência do outline: ausente (amarelo) > em chat (rosa) > online
 * (verde). `offline`, `null` (estado fora do vocabulário) ou sem presença → `null`
 * = FORA: nunca mostrar offline como presente.
 */
export function estadoNoCanal(status: PresenceStatus | null | undefined, emChat: boolean): EstadoNoCanal | null {
  if (status === 'afk') return 'ausente'
  if (status === 'online') return emChat ? 'em_chat' : 'online'
  return null
}

const ORDEM: Record<EstadoNoCanal, number> = { em_chat: 0, online: 1, ausente: 2 }
const NINGUEM: readonly PresencaNoCanal[] = []
const NINGUEM_EM_CHAT: ReadonlySet<string> = new Set()

/**
 * Navegação × presença × directory de UM canal. Fora: sem presença viva, sem
 * entrada no directory. `emChat` = quem tem um chat do canal aberto (rosa). Ordem:
 * em chat → online → ausente; desempate `entered_at` ASC. Ninguém é excluído por ser
 * o leitor. Vazio = a MESMA referência.
 */
export function montarPresencaNoCanal(
  usuarios: readonly NavigationUser[],
  presenca: ReadonlyMap<string, UserPresenceEntry>,
  diretorio: ReadonlyMap<string, ISystemUser>,
  emChat: ReadonlySet<string> = NINGUEM_EM_CHAT,
): readonly PresencaNoCanal[] {
  const lista: PresencaNoCanal[] = []
  for (const u of usuarios) {
    const user = diretorio.get(u.user_id)
    if (!user) continue
    const estado = estadoNoCanal(presenca.get(u.user_id)?.presence_status, emChat.has(u.user_id))
    if (!estado) continue
    lista.push({ user, estado, entered_at: u.entered_at })
  }
  if (lista.length === 0) return NINGUEM
  return lista.sort((a, b) => ORDEM[a.estado] - ORDEM[b.estado] || a.entered_at - b.entered_at)
}

/** "N pessoa(s) no canal|chat: Nome (Estado), …" — nome do `role="group"` dos avatares. */
export function rotuloDoGrupo(lista: readonly PresencaNoCanal[], onde: 'canal' | 'chat' = 'canal'): string {
  const n = lista.length
  const quem = lista.map((p) => `${p.user.name} (${ESTADO_NO_CANAL_LABEL[p.estado]})`).join(', ')
  return `${n} ${n === 1 ? 'pessoa' : 'pessoas'} no ${onde}: ${quem}`
}
